<template>
  <div v-click-outside="resetInlineEmojiMenu" class="chat-actions">
    <FriedFloater
      class="text-input-container"
      :main-axis-offset="0"
      :cross-axis-offset="16"
      :show="showInlineEmojiMenu"
      placement="top-start"
    >
      <template #reference>
        <textarea
          ref="inputRef"
          v-model="text"
          class="text-input"
          data-external="chat-text-area-input-field"
          :placeholder="t('write-something')"
          @input="handleInput"
          @keydown.esc="resetInlineEmojiMenu"
          @keydown.left="resetInlineEmojiMenu"
          @keydown.right="resetInlineEmojiMenu"
          @keydown.up="handleNavigate"
          @keydown.down="handleNavigate"
          @keydown.enter.exact.prevent="handleEnter"
          @keydown.tab="handleTab"
        ></textarea>
      </template>
      <FriedInlineEmoji
        :matched-emojis="matchedEmojis"
        :skin-tone="skinTone"
        :selected="selectedInlineEmojiIndex"
        @select="replaceEmojiSearchWithEmoji"
      />
    </FriedFloater>
    <div class="actions">
      <FriedFloater
        v-if="emojisEnabled && emojiIndex"
        v-click-outside="clickOutside"
        class="emoji-wrapper"
        :main-axis-offset="2"
        :cross-axis-offset="16"
        :show="showEmojis"
        placement="top-start"
      >
        <template #reference>
          <FriedIconButton
            v-wave
            data-external="chat-text-area-emoji-picker-button"
            :aria-label="t('show-emoji-picker')"
            :class="['emoji-button', { active: showEmojis, 'is-preview': isPreview }]"
            @click="setShowEmojiPicker(!showEmojis)"
          >
            <FriedIcon class="action-icon emoji-button-icon" icon="smiley" />
          </FriedIconButton>
        </template>
        <FriedEmojiPicker
          :localization="getEmojiLocalization"
          :emoji-index="emojiIndex"
          @select="handleAddEmoji"
          @skin-change="handleSkinChange"
        />
      </FriedFloater>
      <FriedButton
        ref="send"
        v-tooltip="{
          content: t('disabled-in-preview-mode'),
          offset: 8,
          disabled: true,
          show: showSendButtonTooltip,
          container: '.actions',
          placement: Placement.Left,
        }"
        data-external="chat-text-area-send-message-button"
        :compact="true"
        type="button"
        :disabled="!text"
        :aria-label="t('send-message')"
        :class="['send-button', { 'is-preview': isPreview }]"
        @click="handleSend"
      >
        {{ t('send') }}
      </FriedButton>
    </div>
  </div>
</template>

<script lang="ts">
import type { EmojiIndex } from 'emoji-mart-vue-fast';
import { store } from 'emoji-mart-vue-fast';
import type { PropType, Ref } from 'vue';
import { Placement, type Emoji } from '@getaccept/fried-tofu';
import { computed, defineComponent, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { EmojiHelper } from '../../emoji/helpers/emoji.helper';
import { EMOJI_INLINE_TRIGGER_COUNT } from '../../emoji/constants/emoji.constants';
import { ChatHelper } from '../helpers/chat.helper';

export default defineComponent({
  props: {
    isPreview: {
      type: Boolean,
    },
    emojisEnabled: {
      type: Boolean,
    },
    emojiIndex: {
      type: Object as PropType<EmojiIndex>,
      default: null,
    },
  },
  emits: ['send'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const inputRef: Ref<HTMLTextAreaElement> = ref(null);
    const text = ref('');
    const skinTone = ref(0);
    const emojiSearch = ref('');
    const selectedInlineEmojiIndex = ref(0);
    const showSendButtonTooltip = ref(false);
    const showEmojis = ref(false);
    const showInlineEmojiMenu = ref(false);
    const tooltipDelay = ref(3000);

    const matchedEmojis = computed((): Emoji[] => {
      if (!props.emojiIndex) return [];
      return EmojiHelper.getEmojiListByKey(emojiSearch.value, props.emojiIndex);
    });

    const correctEmojiSearch = computed(
      () =>
        emojiSearch.value.charAt(0) === ':' &&
        emojiSearch.value.length >= EMOJI_INLINE_TRIGGER_COUNT &&
        matchedEmojis.value.length > 0 &&
        !emojiSearch.value.includes(' ')
    );

    const handleSkinChange = (newSkinTone: number) => {
      skinTone.value = newSkinTone;
    };

    const isEndingColon = (text: string) => text.charAt(text.length - 1) === ':';

    const handleInput = () => {
      if (!props.emojisEnabled) {
        return;
      }
      emojiSearch.value = getEmojiKey(inputRef.value, text.value);
      const isEndingWithColon = isEndingColon(emojiSearch.value);
      selectedInlineEmojiIndex.value = 0;

      if (isEndingWithColon && correctEmojiSearch.value) {
        const emoji = props.emojiIndex.findEmoji(emojiSearch.value);
        replaceEmojiSearchWithEmoji(emoji);
        return;
      }

      if (correctEmojiSearch.value && !showInlineEmojiMenu.value) {
        setShowInlineEmojiMenu(true);
        return;
      }
      if (!correctEmojiSearch.value) {
        resetInlineEmojiMenu();
      }
    };

    const getEmojiKey = (element: HTMLTextAreaElement, text: string) => {
      const end: number = element.selectionEnd;
      const start: number = text.lastIndexOf(':', end - 2);
      return start >= 0 ? text.substring(start, end) : '';
    };

    const resetInlineEmojiMenu = () => {
      setShowInlineEmojiMenu(false);
      emojiSearch.value = '';
      selectedInlineEmojiIndex.value = 0;
    };

    const setShowInlineEmojiMenu = (show: boolean) => {
      showInlineEmojiMenu.value = show;
    };

    const setShowEmojiPicker = (show: boolean) => {
      showEmojis.value = show;
    };

    const handleSendButtonTooltip = () => {
      if (showSendButtonTooltip.value) {
        return;
      }
      showSendButtonTooltip.value = true;
      setTimeout(() => {
        showSendButtonTooltip.value = false;
      }, tooltipDelay.value);
    };

    const handleAddEmoji = async (emoji: Emoji) => {
      const cursorPosition: number = inputRef.value?.selectionEnd;
      setShowEmojiPicker(false);
      text.value =
        text.value.slice(0, cursorPosition) + emoji.native + text.value.slice(cursorPosition);
      inputRef.value?.focus();
      await nextTick();
      inputRef.value?.setSelectionRange(
        cursorPosition + emoji.native.length,
        cursorPosition + emoji.native.length
      );
    };

    const replaceEmojiSearchWithEmoji = async (emoji: Emoji) => {
      const nativeEmoji: string = EmojiHelper.getEmojiWithSkinTone(emoji, skinTone.value)?.native;
      if (!nativeEmoji) {
        resetInlineEmojiMenu();
        return;
      }
      const cursorPosition: number =
        inputRef.value?.selectionEnd - emojiSearch.value.length + nativeEmoji.length;
      text.value = text.value.replace(emojiSearch.value, nativeEmoji);
      resetInlineEmojiMenu();
      await nextTick();
      inputRef.value?.setSelectionRange(cursorPosition, cursorPosition);
    };

    const handleSend = () => {
      if (props.isPreview) {
        handleSendButtonTooltip();
        return;
      }
      setFocus();
      if (text.value) {
        emit('send', EmojiHelper.encode(text.value));
        text.value = '';
      }
    };

    const setFocus = () => {
      inputRef.value?.focus();
    };

    const clickOutside = () => {
      if (!showEmojis.value) {
        return;
      }
      setShowEmojiPicker(false);
    };

    const handleEnter = () => {
      if (showInlineEmojiMenu.value) {
        replaceEmojiSearchWithEmoji(matchedEmojis.value[selectedInlineEmojiIndex.value]);
      } else {
        handleSend();
      }
    };

    const handleTab = (event: KeyboardEvent) => {
      if (showInlineEmojiMenu.value) {
        event.preventDefault();
        replaceEmojiSearchWithEmoji(matchedEmojis.value[selectedInlineEmojiIndex.value]);
      }
    };

    const handleNavigate = (event: KeyboardEvent) => {
      if (!showInlineEmojiMenu.value) {
        return;
      }
      event.preventDefault();
      const firstSelected: boolean = selectedInlineEmojiIndex.value === 0;
      const lastSelected: boolean =
        selectedInlineEmojiIndex.value === matchedEmojis.value.length - 1;

      let step: number;
      switch (event.key) {
        case 'ArrowUp':
          if (firstSelected) {
            return;
          }
          step = -1;
          break;
        case 'ArrowDown':
          if (lastSelected) {
            return;
          }
          step = 1;
          break;
      }

      selectedInlineEmojiIndex.value += step;
    };

    onMounted(() => {
      skinTone.value = store.get('skin');
    });

    return {
      inputRef,
      resetInlineEmojiMenu,
      showInlineEmojiMenu,
      text,
      handleInput,
      handleNavigate,
      handleEnter,
      handleTab,
      matchedEmojis,
      skinTone,
      selectedInlineEmojiIndex,
      replaceEmojiSearchWithEmoji,
      clickOutside,
      showEmojis,
      setShowEmojiPicker,
      getEmojiLocalization: ChatHelper.getEmojiLocalization(),
      handleAddEmoji,
      handleSkinChange,
      showSendButtonTooltip,
      handleSend,
      t,
      correctEmojiSearch,
      emojiSearch,
      getEmojiKey,
      setShowInlineEmojiMenu,
      handleSendButtonTooltip,
      setFocus,
      isEndingColon,
      Placement
    };
  },
});
</script>

<style lang="scss" scoped>
.chat-actions {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--spacing-50);
  justify-content: space-between;
  padding: var(--spacing-50);
  box-sizing: border-box;
  border-top: 1px solid var(--gray-94);

  .text-input {
    border: none;
    overflow: auto;
    outline: none;
    box-shadow: none;
    resize: none;
    height: 100%;
    width: 100%;
    margin: 0;
    font-size: 0.875rem;
    padding: var(--spacing-50);
  }

  .text-input-container {
    height: 100%;
    width: 100%;
    position: relative;
  }

  .actions {
    display: flex;
    align-items: center;
    width: 100%;

    .emoji-wrapper {
      position: relative;
    }

    .send-button {
      width: 100%;
    }

    .send-button,
    .emoji-button {
      &.is-preview {
        cursor: auto;
      }
    }
  }
}
</style>
